import React from 'react';
import { Box, Button, Tabs, TabList, Tab, Typography } from '@mui/joy';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import {
  BusinessOutlined,
  ConfirmationNumberOutlined,
  SupervisorAccountOutlined,
  ShoppingCartOutlined,
  ContactPhoneOutlined,
  DeveloperBoardOutlined,
  AssessmentOutlined,
  DashboardOutlined,
} from '@mui/icons-material';
import { AdminAccountsPanel } from './accounts/AdminAccountsPanel';
import SupportDashboardPage from '../SupportDashboardPage';
import { AdminAffiliateDashboard } from '../affiliates';
import CallBuddyReports from '../callbuddy/CallBuddyReports';
import { AdminBacklinkOrdersDashboard } from './AdminBacklinkOrdersDashboard';
import { AdminCrmPanel } from './crm/AdminCrmPanel';
import { AdminDevelopmentPanel } from './development/AdminDevelopmentPanel';
import { AdminScorecardPanel } from './scorecard/AdminScorecardPanel';
import { PageHelpButton } from '../../components/knowledge-base/PageHelpButton';

import { AdminDashboard } from './dashboard/AdminDashboard';

type TabKey = 'dashboard' | 'scorecard' | 'accounts' | 'crm' | 'support' | 'development' | 'orders' | 'affiliates';
const TABS: { key: TabKey; label: string; path: string; icon: React.ReactNode }[] = [
  { key: 'dashboard', label: 'Dashboard', path: '/manage/dashboard', icon: <DashboardOutlined /> },
  { key: 'scorecard', label: 'Scorecard', path: '/manage/scorecard', icon: <AssessmentOutlined /> },
  { key: 'accounts', label: 'Accounts', path: '/manage/accounts', icon: <BusinessOutlined /> },
  { key: 'crm', label: 'Sales', path: '/manage/crm', icon: <ContactPhoneOutlined /> },
  { key: 'support', label: 'Support', path: '/manage/support', icon: <ConfirmationNumberOutlined /> },
  { key: 'development', label: 'Development', path: '/manage/development', icon: <DeveloperBoardOutlined /> },
  { key: 'orders', label: 'Orders', path: '/manage/orders', icon: <ShoppingCartOutlined /> },
  { key: 'affiliates', label: 'Affiliates', path: '/manage/affiliates', icon: <SupervisorAccountOutlined /> },
];

/**
 * Unified SuperAdmin hub. The active tab is derived from the URL so deep links
 * (and the existing /manage/* routes) keep working, while the TabList lets a
 * superadmin switch between admin tools in one click. Existing admin pages are
 * mounted as-is inside their panels.
 */
export const AdminHubPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const active: TabKey =
    TABS.find((t) => location.pathname.startsWith(t.path))?.key ?? (location.pathname === '/manage/callbuddy' ? 'crm' : 'dashboard');

  if (location.pathname.startsWith('/manage/organizations')) return <Navigate replace to={`/manage/accounts${location.search}`} />;
  if (location.pathname.startsWith('/manage/onboarding')) {
    const oldView = location.pathname.split('/')[3] || 'tasks';
    const next = ({ tasks: '/tasks', customers: '', sequences: '/settings/sequences', nps: '/settings/nps', settings: '/settings/onboarding' } as Record<string, string>)[oldView] ?? '';
    return <Navigate replace to={`/manage/accounts${next}${location.search}`} />;
  }
  const callReports = location.pathname === '/manage/callbuddy' || location.pathname === '/manage/crm/calls';
  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Typography level="h2">Admin</Typography>
        <PageHelpButton />
      </Box>
      <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 2 }}>
        Your workspace for growing accounts, helping customers, and improving ApexOS.
      </Typography>

      <Tabs
        value={active}
        onChange={(_, v) => {
          const tab = TABS.find((t) => t.key === v);
          if (tab && tab.key !== active) navigate(tab.path);
        }}
        sx={{ bgcolor: 'transparent', minWidth: 0 }}
      >
        <TabList aria-label="Admin sections" sx={{ overflowX: 'auto', flexWrap: 'nowrap', scrollbarWidth: 'thin', gap: .5, '& > *': { flexShrink: 0, minHeight: 44, px: 1.5, fontSize: 14 } }}>
          {TABS.map((t) => (
            <Tab key={t.key} value={t.key}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box component="span" sx={{display:{xs:'none',xl:'flex'}}}>{t.icon}</Box>
                {t.label}
              </Box>
            </Tab>
          ))}
        </TabList>
      </Tabs>

      <Box sx={{ mt: 3 }}>
        {active === 'dashboard' && <AdminDashboard />}
        {active === 'scorecard' && <AdminScorecardPanel />}
        {active === 'accounts' && <AdminAccountsPanel />}
        {active === 'support' && (
          // The support dashboard is a full-height two-panel screen (list +
          // conversation with a pinned reply composer). It needs a bounded
          // height for its internal scrolling to work inside this page.
          <Box
            sx={{
              height: 'calc(100vh - 340px)',
              minHeight: 540,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 'lg',
              overflow: 'hidden',
              bgcolor: 'background.surface',
            }}
          >
            <SupportDashboardPage />
          </Box>
        )}
        {active === 'crm' && <><Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}><Button variant="plain" onClick={() => navigate(callReports ? '/manage/crm' : '/manage/crm/calls')}>{callReports ? 'Back to sales board' : 'Call reports'}</Button></Box>{callReports ? <CallBuddyReports /> : <AdminCrmPanel />}</>}
        {active === 'development' && <AdminDevelopmentPanel />}
        {active === 'affiliates' && <AdminAffiliateDashboard />}
        {active === 'orders' && <AdminBacklinkOrdersDashboard />}
      </Box>
    </Box>
  );
};

export default AdminHubPage;
